import { useMemo, useRef } from 'react';
import { Feature, UserSetting } from 'src/WebApiController';

import { useUserHasFeature } from './useUserHasFeature';
import { useServerUserSetting } from './useUserSetting';

export const useFavoriteQuickFilter = (settingId: UserSetting | undefined) => {
  const hasFeature = useUserHasFeature(Feature.DefaultSavedFilterInventory);

  const {
    value: _favoriteQuickFilterId,
    setUserSetting: _setFavoriteQuickFilterId,
  } = useServerUserSetting<string | null>({
    id: settingId,
  });

  const favoriteQuickFilterId = useMemo(() => {
    if (!hasFeature || settingId === undefined) {
      return null;
    }
    return _favoriteQuickFilterId ?? null;
  }, [hasFeature, settingId, _favoriteQuickFilterId]);

  const favoriteQuickFilterIdRef = useRef(favoriteQuickFilterId);
  favoriteQuickFilterIdRef.current = favoriteQuickFilterId;
  const setFavoriteQuickFilter = useMemo(() => {
    if (!hasFeature || settingId === undefined) {
      return undefined;
    }
    return (filterId: string | null) => {
      if (filterId === favoriteQuickFilterIdRef.current) {
        filterId = null; // Unset if the same filter is selected
      }
      _setFavoriteQuickFilterId(filterId);
    };
  }, [hasFeature, settingId, _setFavoriteQuickFilterId]);

  // Return null and noop setter if the feature is not enabled or settingId is not provided
  return useMemo(
    () => ({
      favoriteQuickFilterId,
      setFavoriteQuickFilter,
      hasFeature,
    }),
    [favoriteQuickFilterId, setFavoriteQuickFilter, hasFeature]
  );
};
